<template>
  <div>
    <span v-if="getScenarioInCurrentLocale()">
      <h4>
        {{ getScenarioInCurrentLocale().reporting_team_mean_preferences_title }}
      </h4>
      <div
        v-html="
          getScenarioInCurrentLocale()
            .reporting_team_mean_preferences_description
        "
        style="max-width: 900px"
      />
      <br />
    </span>
    <ECharts
      :initOptions="initOptions"
      ref="teamMostLiked"
      :options="teamMostLikedOptions"
      :autoresize="false"
      :style="getEChartsStyle()"
    />
    <img v-if="chartsReady" :src="teamMostLikedImage()" width="800" />
    <br />
    <span v-if="getScenarioInCurrentLocale()">
      <h4>
        {{ getScenarioInCurrentLocale().reporting_diversity_title }}
      </h4>
      <div
        v-html="getScenarioInCurrentLocale().reporting_diversity_description"
        style="max-width: 900px"
      />
      <br />
    </span>

    <ECharts
      :initOptions="initOptions"
      ref="minMax"
      :options="minMaxOptions"
      :autoresize="false"
      :style="getEChartsStyle()"
    />

    <img v-if="chartsReady" :src="minMaxImage()" width="800" />

    <span v-if="game.enable_survey">
      <br />

      <h3 style="page-break-before: always" v-if="getScenarioInCurrentLocale()">
        {{ getScenarioInCurrentLocale().reporting_survey_answers_graph_title }}
      </h3>

      <div
        v-if="getScenarioInCurrentLocale() && getScenarioInCurrentLocale().reporting_survey_answers_graph_description"
        v-html="getScenarioInCurrentLocale().reporting_survey_answers_graph_description"
        style="max-width: 900px"
      ></div>

      <br />

      <ECharts
        :initOptions="initOptions"
        ref="surveyAnswers"
        :options="surveyAnswersOptions"
        :autoresize="false"
        :style="getEChartsStyle()"
      />

      <img v-if="chartsReady" :src="surveyAnswersImage()" width="800" />
    </span>
  </div>
</template>

<script>
// It would be great if combi reports and normal reports used
// the same component. Unfortunately this is not the case, as
// it's not easy to extract the component from normal report.
import ECharts from "vue-echarts";

const cloneDeep = require("lodash.clonedeep");

export default {
  props: [
    "game",
    "selfEvaluations",
    "numberOfCases",
    "cases",
    "scenario",
    "skipPageBreak",
    "survey1Averages",
    "survey2Averages",
  ],
  components: {
    ECharts,
  },
  data() {
    return {
      teamMostLikedOptions: null,
      initOptions: {
        renderer: "canvas",
      },
      chartsReady: false,
      selfAwarenessStatistics: [],
      sortedSelfAwarenessStatistics: [],
      minMaxOptions: null,
      surveyAnswersOptions: null,
    };
  },
  created() {
    this.calculateTeamSelfAwarenessStatistics();
    this.generateTeamMostLikedOptions();
    this.generateMinMaxOptions();
    if (this.game.enable_survey) {
      this.generateSurveyAnswersOptions();
    }
    // HELPERS
    setTimeout(() => {
      this.chartsReady = true;
    }, 1000);
  },
  methods: {
    minMaxImage() {
      const chart = this.$refs.minMax;
      return chart.getDataURL({
        pixelRatio: 1,
      });
    },
    teamMostLikedImage() {
      const chart = this.$refs.teamMostLiked;
      return chart.getDataURL({
        pixelRatio: 1,
      });
    },
    surveyAnswersImage() {
      const chart = this.$refs.surveyAnswers;
      return chart.getDataURL({
        pixelRatio: 1,
      });
    },
    // SELF EVALUTIONS
    generateTeamMostLikedOptions() {
      const yAxis = [];
      const maxs = [];
      const averages = [];

      for (let i = 1; i <= this.numberOfCases; i++) {
        let sum = 0;
        let count = 0;

        for (let j = 0; j < this.selfEvaluations.length; j++) {
          if (this.selfEvaluations[j].case_number === i) {
            sum += this.selfEvaluations[j].priority;
            count++;
          }
        }
        averages.push({ caseNumber: i, average: sum / count });
      }

      // Let's sort the averages:
      const sortedAverages = averages.sort((a, b) => {
        return b.average - a.average;
      });

      sortedAverages.forEach((a) => {
        const color = this.$store.state.main.colors[Math.round(a.average) - 1];

        const newItem = {
          value: this.numberOfCases - a.average + 1,
          itemStyle: { color: color, borderColor: "black", borderWidth: "1" },
        };

        maxs.push(newItem);

        const thisCase = this.getCaseByCaseNumber(a.caseNumber);
        if (thisCase) {
          // Might not exist in combi reports on the selected locale
          yAxis.push(thisCase.short_name);
        } else {
          yAxis.push("");
        }
      });

      const lessMoreLabels = this.generateLessMoreLabels();

      this.teamMostLikedOptions = {
        animation: false,
        tooltip: {
          show: false,
        },
        yAxis: {
          data: yAxis,
          axisLabel: {
            fontFamily: "Open Sans",
            fontSize: 28,
          },
        },
        grid: {
          left: "10%",
          containLabel: true,
        },
        xAxis: [
          {
            type: "value",
            position: "bottom",
            min: 0,
            max: this.numberOfCases,
            interval: 1,
            axisLabel: {
              fontFamily: "Open Sans",
              fontSize: 28,
            },
          },
          {
            type: "category",
            position: "bottom",
            data: lessMoreLabels,
            offset: 30,
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              fontFamily: "Open Sans",
              fontSize: 24,
            },
          },
        ],
        series: [
          {
            silent: true,
            type: "bar",
            data: maxs,
          },
        ],
      };
    },
    // DIVERSITY
    calculateTeamSelfAwarenessStatistics() {
      // Calculates team self awareness %, as well as min and max for each case and averages
      // Generates an array in the order of cases in the game, and also another array sorted by priority.
      const cases = this.numberOfCases;

      const percentages = [];
      const selectedPlayerPercentages = [];
      const statistics = [];

      for (let i = 1; i <= cases; i++) {
        const aCase = this.getCaseByCaseNumber(i);

        // All self evaluations for this case:
        const selfEvaluations = this.selfEvaluations.filter(
          (evaluation) => evaluation.case_number === i
        );

        // Let's create an array of all priorities
        const priorities = [];

        let sum = 0;

        selfEvaluations.forEach((evaluation) => {
          priorities.push(evaluation.priority);
          sum += evaluation.priority;
        });

        const average = sum / selfEvaluations.length;

        // Sort the priorities. Priorities are strings.
        const sortedPriorities = priorities.sort((a, b) => {
          return a - b;
        });

        const max = sortedPriorities[sortedPriorities.length - 1];
        const min = sortedPriorities[0];

        const newStatistics = {
          case: aCase.case,
          max: max,
          min: min,
          average: average,
        };

        statistics.push(newStatistics);
      }

      this.selfAwarenessStatistics = statistics;

      this.sortedSelfAwarenessStatistics = cloneDeep(
        this.selfAwarenessStatistics
      ).sort((a, b) => {
        return b.average - a.average;
      });
    },
    generateMinMaxOptions() {
      // https://echarts.apache.org/examples/en/editor.html?c=bar-waterfall2
      // https://echarts.apache.org/examples/en/editor.html?c=scatter-simple

      const mins = [];
      const maxs = [];
      const axis = [];

      const cases = this.numberOfCases;

      this.sortedSelfAwarenessStatistics.forEach((minMax) => {
        const { min } = minMax;
        const { max } = minMax;
        let difference = max - min;

        let newMin = cases + 1 - max;

        if (difference === 0) {
          // If there's no difference, let's make some.
          // Otherwise there will be no visible bar.
          newMin -= 0.1;
          difference = 0.2;
        }

        maxs.push(newMin);
        //mins.push(difference);

        const newItem = {
          value: difference,
          itemStyle: {
            color: "#63869e",
            borderColor: "black",
            borderWidth: "1",
          },
        };

        mins.push(newItem);

        const thisCase = this.getCaseByCaseId(minMax.case);

        axis.push(thisCase.short_name + " ");
        // Let's add an extra space, otherwise scatter on 1 will come on top of the axis text
      });

      const lessMoreLabels = this.generateLessMoreLabels();

      this.minMaxOptions = {
        animation: false,
        tooltip: {
          show: false,
        },
        grid: {
          containLabel: true,
        },
        yAxis: {
          type: "category",
          data: axis,
          axisLabel: {
            show: true,
            interval: 0,
            fontFamily: "Open Sans",
            fontSize: 28,
          },
          axisTick: {
            show: true,
            interval: 0,
          },
        },
        xAxis: [
          {
            type: "value",
            position: "bottom",
            data: axis,
            min: 1,
            minInterval: 1,
            maxInterval: 1,
            max: this.numberOfCases,
            axisTick: {
              interval: 1,
            },
            axisLabel: {
              fontFamily: "Open Sans",
              fontSize: 28,
            },
          },
          {
            type: "category",
            position: "bottom",
            data: lessMoreLabels,
            offset: 40,
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              fontFamily: "Open Sans",
              fontSize: 28,
            },
          },
        ],
        series: [
          {
            silent: true,
            type: "bar",
            stack: "minmax",
            itemStyle: {
              normal: {
                color: "rgba(0,0,0,0)",
              },
            },
            data: maxs,
          },
          {
            silent: true,
            type: "bar",
            stack: "minmax",
            data: mins,
          },
        ],
      };
    },
    // SURVEY ANSWERS
    generateSurveyAnswersOptions() {
      const averages = [];

      for (let i = 0; i < this.numberOfCases; i++) {
        const aCase = this.getCasesInCurrentLocale()[i];

        const newAverage = {
          case: aCase,
          caseNumber: aCase.case_number,
          caseName: aCase.short_name,
          average1: this.surveyAverage(1, aCase),
          average2: this.surveyAverage(2, aCase),
        };

        averages.push(newAverage);
      }

      // Let's sort averages by priority (= question 2)
      const sortedAverages = cloneDeep(averages).sort((a, b) => {
        return b.average2 - a.average2;
      });

      const yAxis = [];
      const average1Series = [];
      const average2Series = [];

      for (let i = sortedAverages.length - 1; i >= 0; i--) {
        const average = sortedAverages[i];
        yAxis.push(average.caseName);
        average1Series.push(average.average1);
        average2Series.push(average.average2);
      }

      this.surveyAnswersOptions = {
        animation: false,
        tooltip: {
          show: false,
        },
        legend: {
          textStyle: {
            fontFamily: "Open Sans",
            fontSize: 28,
          },
        },
        grid: {
          left: "10%",
          containLabel: true,
        },
        xAxis: {
          type: "value",
          min: 0,
          minInterval: 1,
          maxInterval: 1,
          max: this.game.surveyScale,
          axisTick: {
            interval: 1,
          },
          axisLabel: {
            fontFamily: "Open Sans",
            fontSize: 28,
          },
        },
        yAxis: {
          type: "category",
          data: yAxis,
          axisLabel: {
            fontFamily: "Open Sans",
            fontSize: 28,
          },
        },
        series: [
          {
            animation: false,
            hoverAnimation: false,
            legendHoverLink: false,
            silent: true,
            name:
              this.$t("answer2legend") +
              " (1 = " +
              this.getScenarioInCurrentLocale().survey_question_2_minimum_value_label +
              ", " +
              this.game.survey_scale +
              " = " +
              this.getScenarioInCurrentLocale().survey_question_2_maximum_value_label +
              ")",
            type: "bar",
            data: average2Series,
            itemStyle: {
              color: "#ccccff",
              borderColor: "black",
              borderWidth: "1",
            },
          },
          {
            animation: false,
            hoverAnimation: false,
            legendHoverLink: false,
            silent: true,
            name:
              this.$t("answer1legend") +
              " (1 = " +
              this.getScenarioInCurrentLocale().survey_question_1_minimum_value_label +
              ", " +
              this.game.survey_scale +
              " = " +
              this.getScenarioInCurrentLocale().survey_question_1_maximum_value_label +
              ")",
            type: "bar",
            data: average1Series,
            itemStyle: {
              color: "#63869e",
              borderColor: "black",
              borderWidth: "1",
            },
          },
        ],
      };
    },
    // HELPERS
    surveyAverage(surveyNumber, aCase) {
      // For players we have stored averages
      if (surveyNumber === 1) {
        return this.survey1Averages[aCase.case_number];
      } else {
        return this.survey2Averages[aCase.case_number];
      }
    },

    getEChartsStyle() {
      return {
        position: "absolute",
        left: "-9999px",
        top: "-9999px",
        width: "2000px",
        height: "1000px",
      };
    },
    getCasesInCurrentLocale() {
      // Order by case number
      let allCasesInLocale = cloneDeep(
        this.cases.filter((aCase) => aCase.locale === this.$i18n.locale)
      );

      if (!allCasesInLocale.length) {
        const locale = this.cases[0].locale;

        allCasesInLocale = cloneDeep(
          this.cases.filter((aCase) => aCase.locale === locale)
        );        
      }

      const sortedCases = allCasesInLocale.sort((a, b) => {
        return a.case_number - b.case_number;
      });
      return sortedCases;
    },
    getCaseByCaseId(id) {
      return this.getCasesInCurrentLocale().find((aCase) => aCase.case === id);
    },
    getCaseByCaseNumber(caseNumber) {
      return this.getCasesInCurrentLocale().find(
        (aCase) => aCase.case_number === caseNumber
      );
    },
    getScenarioInCurrentLocale() {
      let scenario = this.scenario.find(
        (sc) => sc.locale === this.$i18n.locale
      );

      if (scenario) {
        return scenario;
      } else {
        if (this.cases && this.cases.length > 0) {
            const locale = this.cases[0].locale;

            scenario = this.scenario.find(
                (sc) => sc.locale === locale
            );
        }
        return scenario;
      }
    },
    generateLessMoreLabels() {
      const lessMoreLabels = [];

      if (!this.getScenarioInCurrentLocale()) {
        // May happen in combined reports
        lessMoreLabels.push("");
        lessMoreLabels.push("");
        lessMoreLabels.push("");
        return lessMoreLabels;
      }
      lessMoreLabels.push(
        this.getScenarioInCurrentLocale().self_evaluation_unpreferred
      );
      lessMoreLabels.push("");
      lessMoreLabels.push(
        this.getScenarioInCurrentLocale().self_evaluation_preferred
      );

      return lessMoreLabels;
    },
  },
};
</script>
<i18n>
{
  "en": {
    "answer1legend": "Perceived level",
    "answer2legend": "Perceived importance"
  },
  "fi": {
    "answer1legend": "Arvioitu taso",
    "answer2legend": "Arvioitu tärkeys"
  },
  "sv": {
    "answer1legend": "Upplevd nivå",
    "answer2legend": "Upplevd vikt"
  },
  "de": {
    "answer1legend": "Wie gut geht es",
    "answer2legend": "Bedeutung"
  },
  "ko": {
    "answer1legend": "우리 팀이 인지하고 있는 현재 수준",
    "answer2legend": "우리 팀이 인지하고 있는 중요도"
  }
}
  
</i18n>
